<template>
  <div>
    <h1 class="page-title">Check-in</h1>

    <div class="search-box">
      <input class="input" type="text" v-model="searchQuery" placeholder="Buscar participante..." />
    </div>

    <SkeletonLoader v-if="store.loading" variant="table-row" :count="6" />

    <div class="checkin-list" v-else-if="filteredPending.length > 0">
      <div class="checkin-item card" v-for="item in filteredPending" :key="item.id">
        <div class="checkin-info">
          <span class="checkin-name">{{ item.name }}</span>
          <span class="checkin-meta">{{ item.leader }} · {{ item.celula || '-' }}</span>
        </div>
        <button class="btn-checkin" @click="openCheckinModal(item)">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19L21 7l-1.41-1.41L9 16.17z"/></svg>
          <span>Check-in</span>
        </button>
      </div>
    </div>

    <EmptyState
      v-else
      title="Todos confirmados!"
      description="Nao ha participantes pendentes de check-in."
    />

    <!-- Modal Checkin -->
    <div class="modal-overlay" v-if="checkinTarget" @click.self="closeModal">
      <div class="modal-card">
        <h2 class="modal-title">Check-in: {{ checkinTarget.name }}</h2>

        <div class="form-group">
          <label class="label">Bagagens *</label>
          <input class="input" type="number" min="0" v-model.number="checkinForm.baggage" placeholder="Numero de volumes" />
        </div>

        <div class="form-group">
          <label class="label">Alergia / Medicacao</label>
          <input class="input" type="text" v-model="checkinForm.allergy" placeholder="Ex: amendoim, insulina..." />
        </div>

        <div class="form-group">
          <label class="label">Fotos da mala (opcional)</label>
          <div class="baggage-photos">
            <div class="photo-slot" v-for="(photo, idx) in checkinForm.photos" :key="idx">
              <img :src="photo.preview" alt="Mala" class="photo-preview" />
              <button type="button" class="photo-remove" @click="removePhoto(idx)" aria-label="Remover foto">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
              </button>
            </div>
            <label class="photo-slot photo-add" v-if="checkinForm.photos.length < 2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M20 5h-3.17L15 3H9L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-8 13c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5z"/></svg>
              <span>Adicionar</span>
              <input type="file" accept="image/*" capture="environment" class="file-input-hidden" @change="onPhotoSelected" />
            </label>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn-secondary" @click="closeModal">Cancelar</button>
          <button class="btn-primary btn-confirm" @click="confirmCheckin" :disabled="checkinForm.baggage === null || checkinForm.baggage === '' || confirming">
            <span v-if="!confirming">Confirmar Check-in</span>
            <span v-else>Confirmando...</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useParticipantsStore } from '../stores/participants'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import EmptyState from '../components/ui/EmptyState.vue'

export default {
  name: 'CheckInPage',
  components: { SkeletonLoader, EmptyState },
  data() {
    return {
      searchQuery: '',
      checkinTarget: null,
      checkinForm: { baggage: 0, allergy: '', photos: [] },
      confirming: false
    }
  },
  computed: {
    store() { return useParticipantsStore() },
    filteredPending() {
      const items = this.store.pending
      if (!this.searchQuery.trim()) return items
      const normalize = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      const q = normalize(this.searchQuery)
      return items.filter(i => normalize(i.name).includes(q))
    }
  },
  methods: {
    openCheckinModal(item) {
      this.checkinTarget = item
      this.checkinForm = {
        baggage: item.baggage ?? 0,
        allergy: item.allergy || '',
        photos: []
      }
    },
    closeModal() {
      this.checkinForm.photos.forEach(p => p.preview && URL.revokeObjectURL(p.preview))
      this.checkinForm.photos = []
      this.checkinTarget = null
    },
    async onPhotoSelected(event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file || this.checkinForm.photos.length >= 2) return
      try {
        const normalized = await this.normalizePhoto(file)
        const preview = URL.createObjectURL(normalized)
        this.checkinForm.photos.push({ file: normalized, preview })
      } catch {
        const preview = URL.createObjectURL(file)
        this.checkinForm.photos.push({ file, preview })
      }
    },
    removePhoto(idx) {
      const [removed] = this.checkinForm.photos.splice(idx, 1)
      if (removed?.preview) URL.revokeObjectURL(removed.preview)
    },
    normalizePhoto(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const maxSize = 1600
          let width = img.width
          let height = img.height
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)
          canvas.toBlob((blob) => {
            if (blob) resolve(new File([blob], `mala-${Date.now()}.jpg`, { type: 'image/jpeg' }))
            else reject(new Error('Canvas conversion failed'))
          }, 'image/jpeg', 0.85)
        }
        img.onerror = () => reject(new Error('Image load failed'))
        img.src = URL.createObjectURL(file)
      })
    },
    async confirmCheckin() {
      this.confirming = true
      try {
        const res = await this.store.checkin(
          this.checkinTarget.id,
          { baggage: this.checkinForm.baggage, allergy: this.checkinForm.allergy },
          { newPhotos: this.checkinForm.photos.map(p => p.file) }
        )
        const warn = res?.photoUploadWarning
        if (warn) {
          this.$toast.open({ message: warn, type: 'warning', position: 'top-right', duration: 5000 })
        }
        this.closeModal()
      } catch {
        this.$toast.open({ message: 'Erro ao realizar check-in', type: 'error', position: 'top-right', duration: 5000 })
      } finally {
        this.confirming = false
      }
    }
  }
}
</script>

<style scoped>
.search-box {
  max-width: 400px;
  margin-bottom: var(--spacing-base);
}
.checkin-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}
.checkin-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-base);
  transition: var(--transition-fast);
}
.checkin-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}
.checkin-name {
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--color-text-primary);
}
.checkin-meta {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
.btn-checkin {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 8px 16px;
  background: var(--color-success);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;
}
.btn-checkin:hover { background: #27ae60; box-shadow: var(--shadow-button); }
.form-group {
  margin-bottom: var(--spacing-base);
}
.btn-confirm {
  background: var(--color-success);
  border-color: var(--color-success);
}
.btn-confirm:hover { background: #27ae60; border-color: #27ae60; }
.btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }

/* Fotos da mala */
.baggage-photos {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  margin-top: var(--spacing-xs);
}
.photo-slot {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-bg-separator);
  flex-shrink: 0;
}
.photo-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.photo-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.65);
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.photo-remove:hover { background: rgba(0,0,0,0.85); }
.photo-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 2px dashed #bbb;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: var(--text-xs);
  transition: var(--transition-fast);
}
.photo-add:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}
.file-input-hidden {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
</style>
